import React from "react";
import { useLocation } from "react-router-dom";
import { Box, Drawer, IconButton } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ActuateLogoUrl from "url:../../../assets/AcuateLogo.png";
import { NavBar } from "./NavBar";
import {
  MOBILE_TOP_BAR_PX,
  SIDEBAR_WIDTH_PX,
  useIsMobileShell,
} from "./navLayout";
import { Dispatcher } from "../pages/Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";

/**
 * The NavBar for pages that have been made responsive. On desktop it is the
 * usual sidebar. On small screens it is a top bar whose menu button opens the
 * same NavBar (expanded) in a drawer, so every link, permission gate and
 * feature flag stays in one place. The drawer closes on navigation.
 */
export const ResponsiveNavBar = ({
  navBarCollapsed,
  setNavBarCollapsed,
}: {
  navBarCollapsed: boolean;
  setNavBarCollapsed: Dispatcher<boolean>;
}) => {
  const mobile = useIsMobileShell();
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  if (!mobile) {
    return (
      <NavBar
        navBarCollapsed={navBarCollapsed}
        setNavBarCollapsed={setNavBarCollapsed}
        props={{}}
      />
    );
  }

  // Inside the drawer the NavBar's own collapse button just closes it.
  const closeInsteadOfCollapse: Dispatcher<boolean> = () => setOpen(false);

  return (
    <>
      <Box
        component="header"
        data-testid="mobile-top-bar"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: `${MOBILE_TOP_BAR_PX}px`,
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          backgroundColor: "#001943",
          zIndex: 1100,
        }}
      >
        <IconButton
          aria-label="Open menu"
          data-testid="mobile-menu-button"
          onClick={() => setOpen(true)}
          sx={{ color: "#fff" }}
        >
          <MenuIcon />
        </IconButton>
        <img src={ActuateLogoUrl} alt="Actuate" style={{ height: 28 }} />
      </Box>
      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: `${SIDEBAR_WIDTH_PX}px`,
            backgroundColor: "#001943",
            overflow: "hidden",
          },
        }}
      >
        <Box
          data-testid="mobile-nav-drawer"
          sx={{ position: "relative", height: "100%" }}
          // Any link closes the drawer, including the page already open
          // (its path does not change, so the location effect never fires).
          onClickCapture={(event) => {
            if ((event.target as HTMLElement).closest("a")) setOpen(false);
          }}
        >
          <NavBar
            navBarCollapsed={false}
            setNavBarCollapsed={closeInsteadOfCollapse}
            props={{}}
          />
        </Box>
      </Drawer>
    </>
  );
};
